/* eslint-disable react/require-default-props */
import React, { FC, ReactNode, useState, useContext } from 'react';
import { NavLink as RouterLink, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { SidebarContext } from 'src/contexts/SidebarContext';
import PropTypes from 'prop-types';
import { Button, Tooltip, Badge, Collapse, ListItem, TooltipProps, tooltipClasses, styled, Menu, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ExpandLessTwoToneIcon from '@mui/icons-material/ExpandLessTwoTone';
import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { resetBidderToggle, emptyRfpDetail, emptyFileSectionFromTemplate, emptyProposalDetail, resetProposalDetailIds } from 'src/utils/dataHandle';
import { resetRfpSaveFile } from 'src/utils/fileHandle';
import { handleResetLegalServiceProposalDetail } from 'src/slices/LegalServiceConnect';
import { useDispatch } from 'react-redux';

interface SidebarMenuItemProps {
  children?: ReactNode;
  link?: string;
  icon?: any;
  badge?: string;
  badgeTooltip?: string;
  open?: boolean;
  active?: boolean;
  name: string;
}

const TooltipWrapper = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip
    {...props}
    classes={{
      popper: className
    }}
  />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.colors.alpha.trueWhite[100],
    color: theme.palette.getContrastText(theme.colors.alpha.trueWhite[100]),
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 'bold',
    borderRadius: theme.general.borderRadiusSm,
    boxShadow: '0 .2rem .8rem rgba(7,9,25,.18), 0 .08rem .15rem rgba(7,9,25,.15)'
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.colors.alpha.trueWhite[100]
  }
}));

// eslint-disable-next-line no-unused-vars
const SidebarMenuItem: FC<SidebarMenuItemProps> = ({ children, link, icon: Icon, badge, badgeTooltip, open: openParent, active, name, ...rest }) => {
  const [menuToggle, setMenuToggle] = useState<boolean>(openParent);
  const [draftAnchor, setDraftAnchor] = useState<null | HTMLElement>(null);
  const {
    t
  }: {
    t: any;
  } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { closeSidebar } = useContext(SidebarContext);

  const toggleMenu = (): void => {
    setMenuToggle((Open) => !Open);
  };

  const handleCloseSidebar = (itemName: string): void => {
    if (itemName === 'Draft New RFP') {
      resetRfpSaveFile();
      resetBidderToggle();
      emptyRfpDetail();
      emptyFileSectionFromTemplate();
    } else if (itemName === 'Proposal Repository') {
      resetProposalDetailIds();
      emptyProposalDetail();
      dispatch<any>(handleResetLegalServiceProposalDetail());
    }
    closeSidebar();
  };

  const handleDraftMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    setDraftAnchor(e.currentTarget);
  };

  const handleDraftMenuClose = () => {
    setDraftAnchor(null);
  };

  const handleDraftOption = (path: string) => {
    handleDraftMenuClose();
    resetRfpSaveFile();
    resetBidderToggle();
    emptyRfpDetail();
    emptyFileSectionFromTemplate();
    closeSidebar();
    navigate(path);
  };

  if (children) {
    return (
      <ListItem component="div" className="Mui-children" key={name} {...rest}>
        <Button
          className={clsx({
            active: menuToggle
          })}
          startIcon={Icon && <Icon />}
          endIcon={menuToggle ? <ExpandLessTwoToneIcon /> : <ExpandMoreTwoToneIcon />}
          onClick={toggleMenu}
        >
          {badgeTooltip ? (
            <TooltipWrapper title={badgeTooltip} arrow placement="right">
              {badge === '' ? <Badge color="primary" variant="dot" /> : <Badge badgeContent={badge} />}
            </TooltipWrapper>
          ) : badge === '' ? (
            <Badge color="primary" variant="dot" />
          ) : (
            <Badge badgeContent={badge} />
          )}
          {t(name)}
        </Button>
        <Collapse in={menuToggle}>{children}</Collapse>
      </ListItem>
    );
  }

  return (
    <ListItem component="div" key={name} {...rest}>
      {name === 'Draft New RFP' ? (
        <>
          <Button disableRipple onClick={handleDraftMenuOpen} startIcon={Icon && <Icon />} endIcon={<KeyboardArrowDownIcon />}>
            {t(name)}
          </Button>
          <Menu
            anchorEl={draftAnchor}
            open={Boolean(draftAnchor)}
            onClose={handleDraftMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <MenuItem onClick={() => handleDraftOption('/corporate/rfp/create/description')}>RFP Drafting</MenuItem>
            <MenuItem onClick={() => handleDraftOption('/corporate/rfp/create/conflict?mode=conflict_only')}>Conflict Check Only First</MenuItem>
          </Menu>
        </>
      ) : (
        <Button
          disableRipple
          component={RouterLink}
          onClick={() => handleCloseSidebar(name)}
          to={link}
          sx={{
            '&.active .MuiButton-startIcon svg, &.active .MuiButton-endIcon svg': {
              color: 'inherit',
              fill: 'currentColor'
            },
            '& .MuiButton-startIcon svg, & .MuiButton-endIcon svg': {
              fill: 'currentColor'
            }
          }}
          startIcon={Icon && <Icon />}
        >
          {t(name)}
          {badgeTooltip ? (
            <TooltipWrapper title={badgeTooltip} arrow placement="right">
              {badge === '' ? <Badge color="primary" variant="dot" /> : <Badge badgeContent={badge} />}
            </TooltipWrapper>
          ) : badge === '' ? (
            <Badge color="primary" variant="dot" />
          ) : (
            <Badge badgeContent={badge} />
          )}
        </Button>
      )}
    </ListItem>
  );
};

SidebarMenuItem.propTypes = {
  children: PropTypes.node,
  active: PropTypes.bool,
  link: PropTypes.string,
  icon: PropTypes.elementType,
  badge: PropTypes.string,
  badgeTooltip: PropTypes.string,
  open: PropTypes.bool,
  name: PropTypes.string.isRequired
};

SidebarMenuItem.defaultProps = {
  open: false,
  active: false
};

export default SidebarMenuItem;
